import { Skeleton } from '@/components/ui/skeleton';
import {
  QuestionLabel,
  answeredValues,
  formatSubmittedAt,
} from '@/features/customers/components/lead-form-parts';
import { getLeadFormResponse } from '@/features/customers/components/lead-form-response';
import { useLead } from '@/features/leads/api/get-lead/get-lead.hook';
import { ChevronDownIcon, ClipboardListIcon } from 'lucide-react';
import { useId, useState } from 'react';

/** How many non-contact answers the in-thread card shows before truncating. */
const MAX_ANSWERS = 6;

/**
 * Pinned at the top of any thread that started from a lead form: which form,
 * when, and what they answered. Renders nothing for a lead that did not come
 * from a Meta lead form, so it is safe to mount for every linked conversation.
 */
export function LeadFormCard({ leadId }: { leadId: string }) {
  const { lead, isLoading } = useLead({ leadId });
  const [expanded, setExpanded] = useState(false);
  const detailsId = useId();

  if (isLoading) return <Skeleton className="h-12 w-full rounded-lg" />;
  const response = lead ? getLeadFormResponse(lead) : null;
  if (!lead || !response) return null;

  const answers = response.answers
    .filter((a) => !a.isContact && a.status === 'answered')
    .slice(0, MAX_ANSWERS);
  const submitted = formatSubmittedAt(response.submittedAt, 'd MMM, h:mm a');

  return (
    <section
      aria-label="Lead form submission"
      className="mx-auto w-full max-w-xl"
    >
      <button
        type="button"
        aria-label={expanded ? 'Hide lead form' : 'Show lead form'}
        aria-expanded={expanded}
        aria-controls={expanded ? detailsId : undefined}
        onClick={() => setExpanded((value) => !value)}
        className="group flex w-full items-center gap-3 rounded-lg py-2 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <span className="flex size-8 shrink-0 items-center justify-center text-muted-foreground">
          <ClipboardListIcon className="size-4" aria-hidden />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[13px] font-medium">
            {response.formName ?? 'Lead form'}
          </span>
          <span className="mt-0.5 block text-xs text-muted-foreground">
            Lead form{submitted ? ` · ${submitted}` : ''}
          </span>
        </span>
        <ChevronDownIcon
          className={`mr-1 size-4 shrink-0 text-muted-foreground transition-transform motion-reduce:transition-none ${expanded ? 'rotate-180' : ''}`}
          aria-hidden
        />
      </button>
      {expanded && (answers.length > 0 || lead.phone) ? (
        <dl
          id={detailsId}
          className="mt-2 grid grid-cols-2 gap-x-4 gap-y-3 pb-3 pl-11 text-[13px]"
        >
          {answers.map((answer) => (
            <div key={answer.key} className="min-w-0">
              <dt className="truncate text-xs text-muted-foreground">
                <QuestionLabel answer={answer} />
              </dt>
              <dd className="font-medium break-words">
                {answeredValues(answer)
                  .map((v) => v.replace(/_/g, ' '))
                  .join(', ')}
              </dd>
            </div>
          ))}
          {lead.phone ? (
            <div className="min-w-0">
              <dt className="text-xs text-muted-foreground">Phone</dt>
              <dd className="font-medium">{lead.phone}</dd>
            </div>
          ) : null}
        </dl>
      ) : null}
    </section>
  );
}
